/**
 * Theming props for the Social Widget.
 * These map to CSS custom properties used internally.
 */
export interface SocialThemingProps {
  /** Container background color */
  socialBackgroundColor?: string;
  /** Primary text color */
  socialTextColor?: string;
  /** Secondary text color */
  socialSecondaryTextColor?: string;
  /** Border color */
  socialBorderColor?: string;
  /** Container border radius */
  socialBorderRadius?: string;
  /** Container padding */
  socialPadding?: string;
  /** Container box shadow */
  socialShadow?: string;
  /** Primary accent color */
  socialPrimaryColor?: string;
  /** Primary hover color */
  socialPrimaryHover?: string;
  /** Card background */
  socialCardBg?: string;
  /** Card border */
  socialCardBorder?: string;
  /** Card border radius */
  socialCardRadius?: string;
  /** Card box shadow */
  socialCardShadow?: string;
  /** Card hover shadow */
  socialCardHoverShadow?: string;
  /** Avatar size */
  socialAvatarSize?: string;
  /** Avatar border radius */
  socialAvatarRadius?: string;
  /** Avatar border */
  socialAvatarBorder?: string;
  /** Post background */
  socialPostBg?: string;
  /** Post border radius */
  socialPostBorderRadius?: string;
  /** Post padding */
  socialPostPadding?: string;
  /** Grid gap */
  socialGridGap?: string;
  /** Grid item border radius */
  socialGridRadius?: string;
  /** Grid overlay background */
  socialGridOverlayBg?: string;
  /** Grid hover scale */
  socialGridHoverScale?: string;
  /** Image border radius */
  socialImageRadius?: string;
  /** Image border */
  socialImageBorder?: string;
  /** Button background */
  socialButtonBg?: string;
  /** Button hover background */
  socialButtonHoverBg?: string;
  /** Button text color */
  socialButtonColor?: string;
  /** Button border radius */
  socialButtonRadius?: string;
  /** Action button background */
  socialActionBg?: string;
  /** Action button hover background */
  socialActionHoverBg?: string;
  /** Action button active background */
  socialActionActiveBg?: string;
  /** Action button active text color */
  socialActionActiveColor?: string;
  /** Action button border radius */
  socialActionRadius?: string;
  /** Action icon size */
  socialActionSize?: string;
  /** Like color */
  socialLikeColor?: string;
  /** Like active background */
  socialLikeActiveBg?: string;
  /** Comment background */
  socialCommentBg?: string;
  /** Comment border */
  socialCommentBorder?: string;
  /** Comment border radius */
  socialCommentRadius?: string;
  /** Font family */
  socialFontFamily?: string;
  /** Username font size */
  socialUsernameSize?: string;
  /** Username font weight */
  socialUsernameWeight?: string;
  /** Timestamp font size */
  socialTimestampSize?: string;
  /** Body text font size */
  socialBodySize?: string;
  /** Meta text font size */
  socialMetaSize?: string;
  /** Divider color */
  socialDividerColor?: string;
  /** Create post card background */
  socialCreateCardBg?: string;
  /** Create post card border */
  socialCreateCardBorder?: string;
  /** Empty state icon color */
  socialEmptyIconColor?: string;
  /** Empty state text color */
  socialEmptyTextColor?: string;
  /** Transition duration */
  socialTransitionDuration?: string;
  /** Hover scale factor */
  socialHoverScale?: string;
  /** Loader spinner color */
  socialLoaderColor?: string;
}

/**
 * Core configuration props for the Social Widget.
 */
export interface SocialWidgetProps extends SocialThemingProps {
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** JWT auth token for authenticated features */
  authToken?: string;
  /** Theme preset. Default: "light" */
  theme?: "light" | "dark";
  /** Layout mode. Default: "feed" */
  layout?: "feed" | "grid";
  /** Number of grid rows (grid layout only). Default: 3 */
  rows?: number;
  /** Number of grid columns (grid layout only). Default: 3 */
  cols?: number;
  /** URL for community page click-throughs (grid layout) */
  communityUrl?: string;
  /** Channel filter for social feed/grid */
  channel?: string;
  /** Widget width CSS value. Default: "600px" */
  width?: string;
  /** Widget height CSS value. Default: "800px" */
  height?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: { widget: string; layout: string; theme: string }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
}

/**
 * Mapping from camelCase theming prop names to the kebab-case query
 * parameter names used by the social widget iframe URL.
 */
export const SOCIAL_THEMING_PROP_TO_PARAM: Record<
  keyof SocialThemingProps,
  string
> = {
  socialBackgroundColor: "social-background-color",
  socialTextColor: "social-text-color",
  socialSecondaryTextColor: "social-secondary-text-color",
  socialBorderColor: "social-border-color",
  socialBorderRadius: "social-border-radius",
  socialPadding: "social-padding",
  socialShadow: "social-shadow",
  socialPrimaryColor: "social-primary-color",
  socialPrimaryHover: "social-primary-hover",
  socialCardBg: "social-card-bg",
  socialCardBorder: "social-card-border",
  socialCardRadius: "social-card-radius",
  socialCardShadow: "social-card-shadow",
  socialCardHoverShadow: "social-card-hover-shadow",
  socialAvatarSize: "social-avatar-size",
  socialAvatarRadius: "social-avatar-radius",
  socialAvatarBorder: "social-avatar-border",
  socialPostBg: "social-post-bg",
  socialPostBorderRadius: "social-post-border-radius",
  socialPostPadding: "social-post-padding",
  socialGridGap: "social-grid-gap",
  socialGridRadius: "social-grid-radius",
  socialGridOverlayBg: "social-grid-overlay-bg",
  socialGridHoverScale: "social-grid-hover-scale",
  socialImageRadius: "social-image-radius",
  socialImageBorder: "social-image-border",
  socialButtonBg: "social-button-bg",
  socialButtonHoverBg: "social-button-hover-bg",
  socialButtonColor: "social-button-color",
  socialButtonRadius: "social-button-radius",
  socialActionBg: "social-action-bg",
  socialActionHoverBg: "social-action-hover-bg",
  socialActionActiveBg: "social-action-active-bg",
  socialActionActiveColor: "social-action-active-color",
  socialActionRadius: "social-action-radius",
  socialActionSize: "social-action-size",
  socialLikeColor: "social-like-color",
  socialLikeActiveBg: "social-like-active-bg",
  socialCommentBg: "social-comment-bg",
  socialCommentBorder: "social-comment-border",
  socialCommentRadius: "social-comment-radius",
  socialFontFamily: "social-font-family",
  socialUsernameSize: "social-username-size",
  socialUsernameWeight: "social-username-weight",
  socialTimestampSize: "social-timestamp-size",
  socialBodySize: "social-body-size",
  socialMetaSize: "social-meta-size",
  socialDividerColor: "social-divider-color",
  socialCreateCardBg: "social-create-card-bg",
  socialCreateCardBorder: "social-create-card-border",
  socialEmptyIconColor: "social-empty-icon-color",
  socialEmptyTextColor: "social-empty-text-color",
  socialTransitionDuration: "social-transition-duration",
  socialHoverScale: "social-hover-scale",
  socialLoaderColor: "social-loader-color",
};

